import { createContext, useContext, useState, useCallback } from "react";

export const translations = {
  pl: {
    nav: { services: "Usługi", about: "O nas", contact: "Kontakt" },
    hero: {
      badge: "Software House · AI-first · Poznań",
      title: "Modern Communication Systems",
      subtitle:
        "Jako firma AI-first tworzymy aplikacje mobilne, webowe oraz oprogramowanie medyczne — każde z nich napędzane zaawansowaną sztuczną inteligencją.",
      ctaPrimary: "Poznaj nasze usługi",
      ctaSecondary: "Skontaktuj się",
    },
    services: {
      overline: "Rozwiązania oparte na AI",
      title: "AI w sercu każdego projektu",
      subtitle:
        "Nie dodajemy AI na końcu — projektujemy wokół niej. Łączymy inżynierię oprogramowania z zaawansowanym uczeniem maszynowym, aby każdy produkt był naprawdę inteligentny.",
      aiBadge: "Napędzane AI",
      mobile: {
        title: "Aplikacje mobilne",
        desc: "Aplikacje na iOS i Android z wbudowanymi modelami AI — od inteligentnych asystentów po personalizację w czasie rzeczywistym.",
      },
      web: {
        title: "Aplikacje webowe",
        desc: "Platformy webowe wzbogacone o AI: automatyzacja, analityka predykcyjna i integracje z dużymi modelami językowymi (LLM).",
      },
      ai: {
        title: "Rozwiązania AI",
        desc: "Rdzeń naszej działalności — modele uczenia maszynowego, autonomiczni agenci AI i automatyzacja procesów szyte na miarę Twojego biznesu.",
        tags: ["Agenci AI", "LLM", "Computer Vision", "Automatyzacja"],
      },
      medical: {
        title: "Oprogramowanie w medycynie",
        desc: "Systemy medyczne wspierane przez AI: diagnostyka obrazowa, analiza danych klinicznych i bezpieczna telemedycyna.",
      },
      education: {
        title: "Rozwiązania edukacyjne",
        desc: "Inteligentne platformy wspierające naukę: adaptacyjne ścieżki kształcenia, tutorzy AI oraz analiza postępów w czasie rzeczywistym.",
      },
    },
    about: {
      overline: "O firmie",
      title: "AI wpisane w nasze DNA",
      p1: "Modern Communication Systems to polski software house z siedzibą w Poznaniu, działający w modelu AI-first. Projektujemy i wdrażamy zaawansowane oprogramowanie napędzane sztuczną inteligencją dla wymagających branż.",
      p2: "Od aplikacji mobilnych i platform webowych, przez autonomicznych agentów AI, aż po systemy medyczne — w każdym rozwiązaniu wykorzystujemy najnowsze modele uczenia maszynowego, aby realnie usprawniać pracę i komunikację.",
      stats: [
        { value: "AI-first", label: "Podejście do produktu" },
        { value: "ML & LLM", label: "Modele i agenci AI" },
        { value: "MedTech", label: "Oprogramowanie medyczne" },
      ],
    },
    contact: {
      overline: "Kontakt",
      title: "Porozmawiajmy o Twoim projekcie",
      subtitle: "Skontaktuj się z nami — odpowiemy najszybciej jak to możliwe.",
      phoneLabel: "Telefon",
      emailLabel: "E-mail",
      addressLabel: "Adres siedziby",
      detailsLabel: "Dane rejestrowe",
    },
    footer: {
      rights: "Wszelkie prawa zastrzeżone.",
      tagline: "Aplikacje mobilne · webowe · AI · medycyna",
    },
  },
  en: {
    nav: { services: "Services", about: "About", contact: "Contact" },
    hero: {
      badge: "Software House · AI-first · Poznań",
      title: "Modern Communication Systems",
      subtitle:
        "As an AI-first company we build mobile and web applications and medical software — each one powered by advanced artificial intelligence.",
      ctaPrimary: "Explore our services",
      ctaSecondary: "Get in touch",
    },
    services: {
      overline: "AI-powered solutions",
      title: "AI at the heart of every project",
      subtitle:
        "We don't bolt AI on at the end — we design around it. We combine software engineering with advanced machine learning so every product is genuinely intelligent.",
      aiBadge: "AI-powered",
      mobile: {
        title: "Mobile apps",
        desc: "iOS and Android apps with built-in AI models — from intelligent assistants to real-time personalisation.",
      },
      web: {
        title: "Web applications",
        desc: "Web platforms enhanced with AI: automation, predictive analytics and large language model (LLM) integrations.",
      },
      ai: {
        title: "AI solutions",
        desc: "The core of what we do — machine learning models, autonomous AI agents and process automation tailored to your business.",
        tags: ["AI agents", "LLM", "Computer Vision", "Automation"],
      },
      medical: {
        title: "Medical software",
        desc: "AI-assisted medical systems: imaging diagnostics, clinical data analysis and secure telemedicine.",
      },
      education: {
        title: "Education solutions",
        desc: "Intelligent learning platforms: adaptive learning paths, AI tutors and real-time progress analytics.",
      },
    },
    about: {
      overline: "About us",
      title: "AI built into our DNA",
      p1: "Modern Communication Systems is a Polish, AI-first software house based in Poznań. We design and deliver advanced, AI-driven software for demanding industries.",
      p2: "From mobile apps and web platforms, through autonomous AI agents, to medical systems — every solution leverages the latest machine learning models to genuinely improve work and communication.",
      stats: [
        { value: "AI-first", label: "Product approach" },
        { value: "ML & LLM", label: "Models & AI agents" },
        { value: "MedTech", label: "Medical software" },
      ],
    },
    contact: {
      overline: "Contact",
      title: "Let's talk about your project",
      subtitle: "Reach out to us — we'll get back to you as soon as possible.",
      phoneLabel: "Phone",
      emailLabel: "E-mail",
      addressLabel: "Registered address",
      detailsLabel: "Company details",
    },
    footer: {
      rights: "All rights reserved.",
      tagline: "Mobile · web · AI · medical software",
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("pl");
  const toggle = useCallback(() => setLang((l) => (l === "pl" ? "en" : "pl")), []);
  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export const COMPANY = {
  name: "MODERN COMMUNICATION SYSTEMS SP. Z O.O.",
  shortName: "Modern Communication Systems",
  krs: "0000441265",
  nip: "PL7773228737",
  regon: "302284564",
  address: "Jeleniogórska 16, 60-179 Poznań, Polska",
  phone: "+48 693 366 163",
  phoneHref: "+48693366163",
  email: "MCS_KONTAKT@HOTMAIL.COM",
  domain: "moderncs.eu",
};

export const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/ce10a2f1be9718418f4e0b792569c38d6d2a0ae4d68096d51d563f7f5ed5495a.png";
export const HERO_URL =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/0f3fdc47c8fd068b2fa5d1abb34d23eb81b88091ae8706789c26139e2769067c.png";
export const IMG_SVC_MOBILE =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/a49ebd277b244a021c37cf946d30a278432d3106f6ddaca10f6f34f7d1dd3cf5.png";
export const IMG_SVC_WEB =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/f9770a7549401a62f8b99498fed74158c6806658ffa84057ebc6f49fff6f85a4.png";
export const IMG_SVC_AI =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/abbccae4fdf7bd65d00bbf0c08dea1bd4363fe8027c347106f199733c2ae8785.png";
export const IMG_SVC_MEDICAL =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/1f7205bb29239cdd77a456a793cf927ca2199675aff55dfc54065e8090fc937d.png";
export const IMG_SVC_EDUCATION =
  "https://static.prod-images.emergentagent.com/jobs/60089dd4-ec3f-4688-908d-27dcaa28e81c/images/b73715121980a9905341c6bee685a4f58f95f6483fccc9cd553b571e679abde7.png";
export const IMG_AI =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwbGlnaHR8ZW58MHx8fHwxNzgyNjQ1NzIzfDA&ixlib=rb-4.1.0&q=85";
export const IMG_MEDICAL =
  "https://images.unsplash.com/photo-1758691463087-43ac1462410f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxtZWRpY2FsJTIwdGVjaG5vbG9neSUyMGJsdWV8ZW58MHx8fHwxNzgyNjQ1NzIyfDA&ixlib=rb-4.1.0&q=85";
export const IMG_ABOUT =
  "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZSUyMHdoaXRlfGVufDB8fHx8MTc4MjY0NTcyM3ww&ixlib=rb-4.1.0&q=85";
